import { Users, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { GtmBrainDoc } from "../api/GtmApi";
import {
  listPersonas,
  parsePersonaList,
  personaSections,
  type PersonaFieldId,
} from "./personas";

type Props = {
  docs: GtmBrainDoc[];
  personaKey: string;
  onDocsChange: (docs: GtmBrainDoc[]) => void;
  onDiscard: () => void;
  onBackToPersonas: () => void;
  onBackToOverview: () => void;
};

export default function BrainPersonaDocView({
  docs,
  personaKey,
  onDocsChange,
  onDiscard,
  onBackToPersonas,
  onBackToOverview,
}: Props) {
  const personas = listPersonas(docs);
  const item = personas.find((p) => p.key === personaKey) || personas[0];
  const sections = item ? personaSections(item.doc) : [];
  const scrollRef = useRef<HTMLDivElement>(null);
  const [listDrafts, setListDrafts] = useState<Record<string, string>>({});

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
    setListDrafts({});
  }, [personaKey]);

  if (!item) {
    return (
      <div className="flex h-full w-[min(100%,28rem)] shrink-0 min-w-0 items-center justify-center border-r border-slate-200 bg-white text-sm text-slate-500">
        Persona not found.
      </div>
    );
  }

  const updateStringField = (field: "name" | "seniority" | "description", value: string) => {
    const next = docs.map((d, i) => (i === item.index ? { ...d, [field]: value } : d));
    onDocsChange(next);
  };

  const updateListField = (field: "topPains" | "wateringHoles", raw: string) => {
    setListDrafts((prev) => ({ ...prev, [field]: raw }));
    const next = docs.map((d, i) =>
      i === item.index ? { ...d, [field]: parsePersonaList(raw) } : d
    );
    onDocsChange(next);
  };

  const listDisplay = (field: "topPains" | "wateringHoles"): string => {
    if (listDrafts[field] !== undefined) return listDrafts[field];
    return (item.doc[field] || []).filter(Boolean).join("\n");
  };

  const scrollToSection = (id: PersonaFieldId) => {
    const el = scrollRef.current?.querySelector(`[data-section-id="${id}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="flex h-full w-[min(100%,28rem)] shrink-0 min-w-0 flex-col border-r border-slate-200 bg-white">
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-5 py-3 text-xs text-slate-500">
        <div className="min-w-0 truncate">
          <button
            type="button"
            className="hover:text-slate-800 hover:underline"
            onClick={onBackToOverview}
          >
            Brain
          </button>
          <span className="mx-1.5 text-slate-300">›</span>
          <button
            type="button"
            className="hover:text-slate-800 hover:underline"
            onClick={onBackToPersonas}
          >
            Personas
          </button>
          <span className="mx-1.5 text-slate-300">›</span>
          <span className="font-medium text-slate-800">{item.label}</span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onDiscard}
            className="rounded-md px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          >
            Discard
          </button>
          <button
            type="button"
            onClick={onBackToPersonas}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6">
        <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-lg bg-violet-50 text-lg">
          {item.emoji}
        </div>
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-violet-600" />
          <h2 className="text-xl font-semibold text-slate-900">Persona</h2>
        </div>
        <p className="mt-1 text-sm text-slate-500">
          Who they are, what hurts, and where you reach them.
        </p>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {sections.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => scrollToSection(s.id)}
              className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:border-slate-300 hover:bg-white hover:text-slate-900"
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-6">
          <section data-section-id="name" className="scroll-mt-4">
            <label className="block text-sm font-semibold text-slate-900">
              Name <span className="font-normal text-rose-500">*</span>
            </label>
            <input
              type="text"
              value={item.doc.name || ""}
              onChange={(e) => updateStringField("name", e.target.value)}
              placeholder="e.g. QA Lead / Test Manager"
              className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300"
            />
          </section>

          <section data-section-id="seniority" className="scroll-mt-4">
            <label className="block text-sm font-semibold text-slate-900">Seniority</label>
            <input
              type="text"
              value={item.doc.seniority || ""}
              onChange={(e) => updateStringField("seniority", e.target.value)}
              placeholder="e.g. Manager, Director, IC"
              className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300"
            />
          </section>

          <section data-section-id="description" className="scroll-mt-4">
            <label className="block text-sm font-semibold text-slate-900">Description</label>
            <textarea
              value={item.doc.description || ""}
              onChange={(e) => updateStringField("description", e.target.value)}
              placeholder="Who they are and what they own."
              rows={4}
              className="mt-2 w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm leading-relaxed text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300"
            />
          </section>

          <section data-section-id="topPains" className="scroll-mt-4">
            <label className="block text-sm font-semibold text-slate-900">Top pains</label>
            <textarea
              value={listDisplay("topPains")}
              onChange={(e) => updateListField("topPains", e.target.value)}
              placeholder={"One per line\nFlaky tests slow releases\nNo visibility into coverage"}
              rows={4}
              className="mt-2 w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm leading-relaxed text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300"
            />
            <p className="mt-1 text-[11px] text-slate-400">One per line (commas also work)</p>
          </section>

          <section data-section-id="wateringHoles" className="scroll-mt-4">
            <label className="block text-sm font-semibold text-slate-900">Watering holes</label>
            <textarea
              value={listDisplay("wateringHoles")}
              onChange={(e) => updateListField("wateringHoles", e.target.value)}
              placeholder={"One per line\nLinkedIn\nMinistry of Testing"}
              rows={4}
              className="mt-2 w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm leading-relaxed text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300"
            />
            <p className="mt-1 text-[11px] text-slate-400">One per line (commas also work)</p>
          </section>
        </div>
      </div>
    </div>
  );
}
